import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEAPActivite4Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const audioRef = useRef(null);

  const slides = [
    // Slide 1 - Titre
    {
      type: 'title',
      title: "Activité 4",
      subtitle: "Collaboration à la réalisation de soins intégrant la qualité et la prévention des risques",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 50%, #5B21B6 100%)"
    },
    // Slide 2 - Sommaire interactif
    {
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Cadre et définition", items: ["Définition de l'activité 4", "Le cadre obligatoire", "Types de collaboration"], color: "#8B5CF6", slideTarget: 3 },
        { num: "2", title: "Protocoles de soins", items: ["DRP (lavage de nez)", "Soins des yeux", "Brûlures", "Morsures", "Chutes et bosses"], color: "#EC4899", slideTarget: 9 },
        { num: "3", title: "Gestion de la fièvre", items: ["Prise de température", "Hyperthermie", "Administration antipyrétique"], color: "#F59E0B", slideTarget: 25 },
        { num: "4", title: "Qualité et traçabilité", items: ["Qualité du soin", "Transmissions", "Message clé"], color: "#10B981", slideTarget: 32 }
      ]
    },
    // Slide 3 - Section Cadre
    {
      type: 'section',
      title: "CADRE ET DÉFINITION",
      subtitle: "Comprendre l'Activité 4",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
    },
    // Slide 4 - Définition
    {
      type: 'content',
      title: "Définition de l'Activité 4",
      content: "L'Activité 4 correspond à des soins en contexte médicalisé. On respecte toujours un protocole et on travaille en collaboration avec un médecin ou une infirmière.",
      highlight: "On n'improvise jamais : on est couvert par un cadre professionnel. La présence peut être physique, par avis téléphonique (SAMU - 15), ou par application d'un écrit professionnel rédigé par la référente santé.",
      image: "https://images.unsplash.com/photo-1761234852163-23f2ededd04e?crop=entropy&cs=srgb&fm=jpg&w=600"
    },
    // Slide 5 - Cadre obligatoire
    {
      type: 'list',
      title: "Le cadre obligatoire (avant tout soin)",
      items: [
        { term: "Protocole écrit validé", desc: "Par la structure, le RSAI, le médecin ou l'IDE" },
        { term: "PAI si pathologie", desc: "Projet d'Accueil Individualisé si risque identifié" },
        { term: "Prescription médicale", desc: "Si traitement ou acte le nécessite" },
        { term: "Accord parental", desc: "Prévu par les documents de la structure" },
        { term: "Traçabilité", desc: "Quoi, quand, qui, comment, suite donnée" }
      ],
      warning: "Sans cadre = pas de soin",
      alertColor: "#EF4444"
    },
    // Slide 6 - Collaboration
    {
      type: 'collaboration',
      title: "Collaboration : 3 situations possibles",
      situations: [
        { num: "1", title: "Médecin/IDE présent(e) physiquement", desc: "Collaboration directe", icon: "👤", color: "#22C55E" },
        { num: "2", title: "Avis téléphonique", desc: "Médecin / IDE / SAMU (15)", icon: "📞", color: "#3B82F6" },
        { num: "3", title: "Application d'un écrit professionnel", desc: "Protocole RSAI/référente santé", icon: "📋", color: "#8B5CF6" }
      ],
      note: "Toujours noter : heure, nom, consignes, suites"
    },
    // Slide 7 - Quand utiliser le protocole
    {
      type: 'content',
      title: "Quand utiliser le protocole ?",
      content: "Devant un problème de santé quel qu'il soit, du bénin au plus grave, vous devez vous référer au protocole. Seuls les produits référencés dans le protocole peuvent être administrés, exception faite des PAI.",
      extraInfo: [
        { term: "Urgences non vitales", desc: "Contacter le médecin référent et/ou la Référente santé" },
        { term: "En cas de doute", desc: "Appeler le SAMU au 15 qui peut donner des conseils téléphoniques" },
        { term: "Important", desc: "Un médecin répond aux appels et ceux-ci sont enregistrés" }
      ],
      image: "https://images.pexels.com/photos/8460035/pexels-photo-8460035.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    // Slide 8 - Repères juridiques
    {
      type: 'list',
      title: "Les repères juridiques",
      items: [
        { term: "Articles L4111-1 et L4311-1", desc: "Confèrent au personnel médical le droit d'administrer des médicaments" },
        { term: "Exercice illégal de la médecine", desc: "Tout autre professionnel s'expose à cette sanction (code pénal)" },
        { term: "Le SAMU", desc: "Seul à pouvoir autoriser une personne à administrer un médicament" },
        { term: "Appels enregistrés", desc: "Font foi en cas de litige" }
      ],
      alertColor: "#DC2626"
    },
    // Slide 9 - Section Protocoles
    {
      type: 'section',
      title: "PROTOCOLES DE SOINS",
      subtitle: "Les gestes techniques",
      gradient: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)"
    },
    // Slide 10 - DRP Introduction
    {
      type: 'content',
      title: "DRP - Désobstruction Rhino-Pharyngée",
      definition: "Lavage de nez - Soin fréquent réalisé uniquement dans le cadre d'un protocole ou écrit.",
      content: "Objectifs : désencombrer les voies respiratoires, faciliter la respiration, assurer le confort de l'enfant.",
      warning: "Risques si mauvaise pratique : fausse route, inconfort majeur, aggravation",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/e4dzlz3g_image.png"
    },
    // Slide 11 - DRP Étapes
    {
      type: 'steps',
      title: "DRP - Les 4 étapes",
      steps: [
        { num: "1", title: "Préparer", items: ["Hygiène des mains", "Sérum physiologique", "Mouchoirs/compresse", "Informer et rassurer l'enfant"], color: "#3B82F6" },
        { num: "2", title: "Installer", items: ["Enfant sur le côté", "Tête légèrement inclinée", "Maintien doux et sécurisé"], color: "#8B5CF6" },
        { num: "3", title: "Réaliser", items: ["Une narine après l'autre", "Respect des quantités du protocole", "Observer la tolérance"], color: "#EC4899" },
        { num: "4", title: "Surveiller & tracer", items: ["Respiration/couleur/toux", "Transmission", "Traçabilité si demandé"], color: "#10B981" }
      ]
    },
    // Slide 12 - DRP Détail technique (infographie)
    {
      type: 'infographic',
      title: "DRP - Déroulé technique",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/p9mq6iem_image.png"
    },
    // Slide 13 - DRP Surveillance
    {
      type: 'list',
      title: "DRP - Surveillance & alertes",
      items: [
        { term: "Observer après le geste", desc: "Respiration, couleur, toux, vomissements, comportement" },
        { term: "Si aggravation ou doute", desc: "Alerter IDE/médecin et suivre le protocole" },
        { term: "Si urgence", desc: "Appel 15 (SAMU) selon consignes et signes d'alerte" },
        { term: "Tracer", desc: "Heure, geste réalisé, réaction, personnes prévenues" }
      ],
      alertColor: "#F59E0B"
    },
    // Slide 14 - Soins des yeux
    {
      type: 'content',
      title: "Soins des yeux",
      definition: "Soin en cas d'écoulement ou conjonctivite suspectée.",
      content: "Le cadre est défini par le protocole et la prévention de la contagion. L'hygiène des mains est indispensable.",
      warning: "1 œil = 1 compresse (jamais la même pour les deux). Nettoyage du plus propre vers le plus sale.",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/sqd9p6vl_image.png"
    },
    // Slide 15 - Soins yeux infographie
    {
      type: 'infographic',
      title: "4 conseils pour nettoyer les yeux de bébé",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/2uakh6u5_image.png"
    },
    // Slide 16 - Conjonctivite
    {
      type: 'content',
      title: "En cas de conjonctivite",
      content: "L'accueil de l'enfant qui présente une conjonctivite est possible dans la mesure où un traitement a été débuté depuis 24H et en fonction de son état.",
      warning: "Son état est incompatible avec la vie en collectivité si sa vue semble modifiée et/ou s'il montre des signes d'inconfort. Il sera alors demandé aux parents de venir récupérer leur enfant.",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/r42660si_image.png"
    },
    // Slide 17 - Brûlure
    {
      type: 'content',
      title: "Brûlure",
      content: "Évaluer la zone touchée, l'étendue, la douleur et la présence de cloques. Conduite à tenir selon protocole : eau tempérée pendant 15 à 20 minutes.",
      warning: "Ne rien appliquer sans prescription (crèmes, corps gras, etc.). Urgence si : visage, mains, organes génitaux ou étendue importante.",
      image: "https://images.unsplash.com/photo-1600531105235-7a82db92fc46?crop=entropy&cs=srgb&fm=jpg&w=600"
    },
    // Slide 18 - Brûlure étapes (infographie)
    {
      type: 'infographic',
      title: "Brûlure - Conduite à tenir",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/e2fnyvpe_image.png"
    },
    // Slide 19 - Morsure
    {
      type: 'listWithImage',
      title: "Morsure",
      items: [
        { term: "Risques", desc: "Infection, saignement, conflit entre familles" },
        { term: "Conduite (protocole)", desc: "Nettoyage eau + savon, désinfection" },
        { term: "Surveillance", desc: "Rougeur, chaleur, douleur, fièvre" },
        { term: "Information", desc: "Informer selon procédure + traçabilité (incident)" },
        { term: "Transmissions", desc: "Factuelles, sans jugement" }
      ],
      alertColor: "#F59E0B",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/93deuuay_image.png"
    },
    // Slide 20 - Chute/Bosse
    {
      type: 'content',
      title: "Chute / Traumatisme crânien",
      content: "Objectif : repérer précocement un signe d'aggravation. Surveiller : vomissements, somnolence, comportement inhabituel.",
      warning: "Ne pas minimiser : une chute peut cacher un traumatisme. Observer avant de manipuler (selon contexte).",
      extraInfo: [
        { term: "Évaluer", desc: "Conscience, douleur, mobilité, plaies" },
        { term: "Si doute", desc: "Avis IDE/médecin ; si signe inquiétant → appel 15" },
        { term: "Tracer", desc: "Heure chute/trauma, signes, surveillance mise en place" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/mgjyzdiv_image.png"
    },
    // Slide 21 - Chute étapes
    {
      type: 'steps',
      title: "Chute / Bosse - Surveillance & décisions",
      steps: [
        { num: "1", title: "Observer", items: ["Conscience, pleurs, douleur", "Mobilité selon l'âge"], color: "#3B82F6" },
        { num: "2", title: "Surveiller", items: ["Vomissements, somnolence", "Comportement inhabituel"], color: "#F59E0B" },
        { num: "3", title: "Alerter", items: ["IDE/médecin selon protocole", "15 si signe inquiétant"], color: "#EF4444" },
        { num: "4", title: "Tracer", items: ["Heure/lieu/circonstances", "Observations factuelles", "Parents informés"], color: "#10B981" }
      ]
    },
    // Slide 22 - Éraflure
    {
      type: 'listWithImage',
      title: "Éraflure",
      items: [
        { term: "Type de soin", desc: "Soin simple qui peut devenir 'médicalisé' selon contexte" },
        { term: "Conduite", desc: "Nettoyage, désinfection, protection (selon protocole)" },
        { term: "Surveillance", desc: "Rougeur, chaleur, suintement, douleur" },
        { term: "Tracer si", desc: "Plaie étendue, saignement, contexte à risque, protocole le demande" }
      ],
      alertColor: "#3B82F6",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/ay59naxk_image.png"
    },
    // Slide 23 - Piqûre insecte
    {
      type: 'listWithImage',
      title: "Piqûre d'insecte",
      items: [
        { term: "Surveillance", desc: "Œdème important, urticaire, gêne respiratoire" },
        { term: "Protocole", desc: "Appliquer le protocole de la structure" },
        { term: "PAI allergie", desc: "Si enfant avec PAI allergie → appliquer immédiatement le protocole" },
        { term: "Transmission", desc: "Transmettre aux parents + tracer si réaction notable" },
        { term: "Urgence", desc: "Alerter / 15 si signes d'anaphylaxie selon consignes" }
      ],
      alertColor: "#EF4444",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/zx6p3b34_image.png"
    },
    // Slide 24 - Homéopathie piqûre
    {
      type: 'content',
      title: "Apis Mellifica 15CH - Piqûres d'insectes",
      definition: "Médicament homéopathique pour les piqûres d'insectes : guêpes, fourmis, abeilles, frelons, bourdons, moustiques.",
      content: "Soulage la douleur, l'œdème, la rougeur, la chaleur et la démangeaison.",
      extraInfo: [
        { term: "Posologie initiale", desc: "5 granules toutes les 10 minutes pendant la première demi-heure" },
        { term: "Puis", desc: "5 granules toutes les heures ensuite" },
        { term: "Jours suivants", desc: "5 granules 3 fois par jour jusqu'à disparition des démangeaisons" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/ufbmer17_image.png"
    },
    // Slide 25 - Section Fièvre
    {
      type: 'section',
      title: "GESTION DE LA FIÈVRE",
      subtitle: "Température et hyperthermie",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
    },
    // Slide 26 - Prise température
    {
      type: 'content',
      title: "Prise de température",
      content: "Pour tous les enfants, la prise de température se fait en axillaire (sous le bras) ou tympanique.",
      extraInfo: [
        { term: "Axillaire", desc: "Placer l'embout sous le bras, au milieu du creux de l'aisselle. Attendre 2 à 3 minutes. Majorer de 0,5°C à la lecture." },
        { term: "Tympanique", desc: "Placer un embout à usage unique. Appliquer dans l'oreille. Prendre la température dans les 2 oreilles." }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/kdz9vbkv_image.png"
    },
    // Slide 27 - Hyperthermie définition
    {
      type: 'content',
      title: "Hyperthermie - Définition",
      definition: "Élévation de la température au-dessus de 38°C, en l'absence d'activité physique intense, chez un enfant normalement couvert, dans une température ambiante tempérée.",
      content: "La fièvre n'est généralement pas dangereuse. L'objectif du traitement est la suppression de l'inconfort et non la normalisation de la température.",
      warning: "Signes cliniques : enfant chaud, yeux rouges et/ou qui larmoient, tremble ou semble fatigué, irritable.",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/mtdv7gdg_image.png"
    },
    // Slide 28 - Hyperthermie conduite
    {
      type: 'list',
      title: "Hyperthermie - Conduite à tenir",
      items: [
        { term: "Dans tous les cas", desc: "Découvrir l'enfant (body ou couche), rafraîchir avec un gant humide, donner à boire" },
        { term: "Contrôler la température", desc: "Utiliser un thermomètre électronique en frontale ou auriculaire" },
        { term: "Surveiller son état", desc: "Comportement habituel vs inhabituel (asthénie, pleurs, prostré)" },
        { term: "Évaluer les signes associés", desc: "Éruption, vomissements, diarrhée, tremblements, pâleur, toux" }
      ],
      alertColor: "#F59E0B"
    },
    // Slide 29 - Fièvre paliers
    {
      type: 'table',
      title: "Fièvre - Conduite selon la température",
      tableData: [
        { label: "37,5°C à 38,5°C (bon état)", value: "Découvrir, recontrôler après 1h, maintenir surveillance", color: "#22C55E" },
        { label: "≥ 38,5°C ou mal tolérée", value: "Recontrôler après 10min, prévenir référente santé, appliquer protocole", color: "#F59E0B" },
        { label: "> 40°C ou convulsions", value: "APPELER LE 15 - URGENCE", color: "#EF4444" }
      ],
      alerts: [
        "Rappeler les parents si mauvaise tolérance ou fièvre persistante",
        "Si PAI concernant l'hyperthermie : l'appliquer rigoureusement"
      ]
    },
    // Slide 30 - Paracétamol
    {
      type: 'content',
      title: "Antipyrétique - Paracétamol",
      definition: "Médicament utilisé dans le traitement des douleurs légères à modérées et/ou dans les états fébriles.",
      content: "Le paracétamol est donné si les conditions citées dans le protocole hyperthermie sont réunies.",
      warning: "Ne pas dépasser une prise toutes les 6 heures."
    },
    // Slide 31 - Paracétamol administration
    {
      type: 'list',
      title: "Paracétamol - Administration",
      items: [
        { term: "Vérifier l'ordonnance", desc: "Signée par le médecin de l'enfant" },
        { term: "Prévenir les parents", desc: "S'assurer qu'il n'y a pas eu de prise dans les 6h précédentes" },
        { term: "Vérifier le poids", desc: "Demander aux parents et contrôler avec la balance" },
        { term: "Vérifier le médicament", desc: "Date de péremption, date d'ouverture (DLC : 6 mois)" },
        { term: "Double validation", desc: "Faire vérifier la dose/poids par une collègue" },
        { term: "Administration", desc: "Introduire la pipette dans le creux de la joue, administrer lentement" }
      ],
      alertColor: "#3B82F6"
    },
    // Slide 32 - Section Qualité
    {
      type: 'section',
      title: "QUALITÉ ET TRAÇABILITÉ",
      subtitle: "Les bonnes pratiques",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)"
    },
    // Slide 33 - Qualité du soin
    {
      type: 'list',
      title: "Qualité du soin",
      items: [
        { term: "Respect strict", desc: "Du protocole ou de l'écrit professionnel" },
        { term: "Sécurité de l'enfant", desc: "Installation, surveillance, prévention" },
        { term: "Bientraitance", desc: "Rassurer, expliquer, respecter l'enfant" },
        { term: "Communication", desc: "Claire et cohérente avec l'équipe et les parents" },
        { term: "Traçabilité", desc: "Indispensable à chaque soin" }
      ],
      alertColor: "#10B981"
    },
    // Slide 34 - Prévention des risques
    {
      type: 'steps',
      title: "Prévention des risques",
      steps: [
        { num: "AVANT", title: "Préparation", items: ["Vérifier protocole / accord / matériel", "Hygiène des mains", "Identifier l'enfant"], color: "#3B82F6" },
        { num: "PENDANT", title: "Réalisation", items: ["Surveillance constante", "Respect des étapes", "Ne pas dépasser son champ de compétences"], color: "#F59E0B" },
        { num: "APRÈS", title: "Suivi", items: ["Surveillance secondaire", "Transmission orale + écrite", "Traçabilité (faits/heure/consignes)"], color: "#10B981" }
      ]
    },
    // Slide 35 - Transmissions
    {
      type: 'list',
      title: "Transmissions & traçabilité",
      items: [
        { term: "Écrit", desc: "Factuel, daté, signé" },
        { term: "Décrire", desc: "Quoi ? Quand ? Où ? Comment ? Réactions ? Suites ?" },
        { term: "Noter les collaborations", desc: "Appel, interlocuteur, consignes" },
        { term: "Éviter les jugements", desc: "Uniquement des observations factuelles" },
        { term: "Objectif", desc: "Assurer continuité des soins et sécurité" }
      ],
      alertColor: "#8B5CF6"
    },
    // Slide 36 - Message clé
    {
      type: 'keyMessage',
      title: "Message clé",
      messages: [
        "Activité 4 = soin en contexte médicalisé",
        "Toujours : protocole / collaboration / écrit professionnel",
        "Jamais d'improvisation",
        "Surveiller – alerter – transmettre – tracer",
        "Objectif : qualité et prévention des risques"
      ],
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
    },
    // Slide 37 - Armoire à pharmacie
    {
      type: 'list',
      title: "Contenu de l'armoire à pharmacie",
      items: [
        { term: "Règles", desc: "Inaccessible, fermée à clé, péremption vérifiée chaque mois" },
        { term: "Matériel", desc: "Gants à usage unique, ciseaux, pince à écharde, poche froid/chaud, compresses stériles" },
        { term: "Pansements", desc: "Hypoallergéniques de différentes tailles, bandes, sparadrap, coton" },
        { term: "Produits", desc: "Antiseptique, dosettes de sérum physiologique" },
        { term: "Médicaments", desc: "Paracétamol (selon ordonnance), homéopathie, médicaments PAI" }
      ],
      alertColor: "#EC4899"
    },
    // Slide 38 - Homéopathie
    {
      type: 'content',
      title: "Homéopathie autorisée",
      content: "Certains médicaments homéopathiques peuvent être administrés selon protocole, en accord avec la famille et validé par le médecin.",
      extraInfo: [
        { term: "Arnica Montana 9CH", desc: "Hématomes (bosses), ecchymoses (bleus), contusions (coups)" },
        { term: "Camilia", desc: "Troubles attribués à la poussée dentaire (fourni par les parents)" },
        { term: "Chamomilla Vulgaris 9CH", desc: "Douleurs gingivales et dentaires (fourni par les parents)" },
        { term: "Apis Mellifica 15CH", desc: "Piqûres d'insectes" }
      ]
    },
    // Slide 39 - PAI
    {
      type: 'content',
      title: "Projet d'Accueil Individualisé (PAI)",
      definition: "Concerne les enfants atteints de maladies chroniques, d'allergies ou d'intolérances alimentaires.",
      content: "Il s'élabore à la demande de la famille et doit s'éditer en collaboration avec le médecin référent et l'infirmière puéricultrice.",
      extraInfo: [
        { term: "Signataires obligatoires", desc: "La famille, la direction de la crèche et le médecin prescripteur" },
        { term: "Validation", desc: "Doit être vu et validé par la référente santé pour former les équipes si nécessaire" },
        { term: "Ordonnance obligatoire", desc: "Nom du/des médicament(s), doses et horaires" },
        { term: "Trousse identifiée", desc: "Au nom et prénom de l'enfant, photo possible" },
        { term: "Réévaluation", desc: "Le PAI doit être réétudié chaque année" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/0otje5y6_image.png"
    },
    // Slide 40 - Soins du siège / Érythème fessier
    {
      type: 'list',
      title: "Érythème fessier",
      items: [
        { term: "Installation", desc: "Serviette sur matelas désinfecté, accompagner l'enfant" },
        { term: "Nettoyage", desc: "Du plus propre au plus sale, de haut en bas sans remonter" },
        { term: "Technique", desc: "Basculer les jambes sur le côté, ne pas soulever" },
        { term: "Application de crème", desc: "Uniquement si tube apporté neuf, avec autorisation des parents et ordonnance" },
        { term: "Fin du soin", desc: "Se laver les mains, désinfecter le plan de change" }
      ],
      alertColor: "#EC4899"
    },
    // Slide 41 - Solution hydroalcoolique
    {
      type: 'content',
      title: "Solution Hydroalcoolique (SHA)",
      definition: "Les SHA ne sont pas une alternative au lavage des mains. Elles s'utilisent entre 2 lavages de mains à l'eau et au savon.",
      content: "Propriétés bactéricides, virucides et fongicides, sans effet nettoyant. Doivent être appliquées sur des mains sèches et non souillées.",
      extraInfo: [
        { term: "Temps de contact", desc: "Minimum 10 à 15 secondes" },
        { term: "Durée totale", desc: "En moyenne 30 secondes" },
        { term: "Conservation", desc: "Noter la date d'ouverture (DLU : 6 mois après ouverture)" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/wcu8uxjc_image.png"
    },
    // Slide 42 - Félicitations
    {
      type: 'congratulations',
      title: "Félicitations !",
      message: "Vous avez terminé le cours sur l'Activité 4 : Collaboration à la réalisation de soins.",
      nextStep: "Testez vos connaissances avec le quiz !",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)"
    }
  ];

  // Quiz questions - 20 questions
  const quizQuestions = [
    {
      id: 1,
      question: "L'Activité 4 correspond :",
      options: [
        "Aux soins d'hygiène quotidiens",
        "À des soins en contexte médicalisé",
        "Aux jeux éducatifs",
        "À l'animation"
      ],
      correct: 1
    },
    {
      id: 2,
      question: "On peut réaliser un soin Activité 4 sans protocole écrit ?",
      options: [
        "Oui",
        "Non"
      ],
      correct: 1
    },
    {
      id: 3,
      question: "La DRP signifie :",
      options: [
        "Détresse Respiratoire Pédiatrique",
        "Désobstruction Rhino-Pharyngée",
        "Désinfection Respiratoire Partielle"
      ],
      correct: 1
    },
    {
      id: 4,
      question: "Après une DRP, on doit :",
      options: [
        "Laisser l'enfant seul",
        "Surveiller la respiration",
        "Donner à manger immédiatement"
      ],
      correct: 1
    },
    {
      id: 5,
      question: "En cas d'aggravation respiratoire :",
      options: [
        "On attend",
        "On appelle le 15 selon protocole",
        "On ne fait rien"
      ],
      correct: 1
    },
    {
      id: 6,
      question: "Une brûlure doit être passée sous eau tempérée pendant :",
      options: [
        "2 minutes",
        "5 minutes",
        "15 à 20 minutes"
      ],
      correct: 2
    },
    {
      id: 7,
      question: "On applique une crème sur une brûlure sans prescription ?",
      options: [
        "Oui",
        "Non"
      ],
      correct: 1
    },
    {
      id: 8,
      question: "Une morsure nécessite :",
      options: [
        "Ignorer",
        "Nettoyage + désinfection",
        "Juste un pansement"
      ],
      correct: 1
    },
    {
      id: 9,
      question: "Après une bosse, on surveille :",
      options: [
        "La couleur des cheveux",
        "Les vomissements",
        "La taille des chaussures"
      ],
      correct: 1
    },
    {
      id: 10,
      question: "Une chute doit toujours être :",
      options: [
        "Minimisée",
        "Observée et tracée",
        "Cachée aux parents"
      ],
      correct: 1
    },
    {
      id: 11,
      question: "Un PAI sert à :",
      options: [
        "Organiser une sortie",
        "Encadrer une pathologie",
        "Gérer les repas"
      ],
      correct: 1
    },
    {
      id: 12,
      question: "En cas d'allergie connue avec PAI :",
      options: [
        "On improvise",
        "On applique strictement le protocole",
        "On attend les parents"
      ],
      correct: 1
    },
    {
      id: 13,
      question: "Une transmission écrite doit être :",
      options: [
        "Subjective",
        "Factuelle et datée",
        "Approximative"
      ],
      correct: 1
    },
    {
      id: 14,
      question: "Lors d'un appel au SAMU, on doit noter :",
      options: [
        "Rien",
        "L'heure et le nom de l'interlocuteur",
        "Seulement la décision"
      ],
      correct: 1
    },
    {
      id: 15,
      question: "Une éraflure peut relever de l'Activité 4 si :",
      options: [
        "Elle est profonde ou à risque",
        "Elle est minime",
        "Elle est invisible"
      ],
      correct: 0
    },
    {
      id: 16,
      question: "La qualité du soin repose sur :",
      options: [
        "Rapidité uniquement",
        "Sécurité, protocole et surveillance",
        "Habitude"
      ],
      correct: 1
    },
    {
      id: 17,
      question: "On peut dépasser son champ de compétences si :",
      options: [
        "On a l'habitude",
        "On pense savoir faire",
        "Jamais"
      ],
      correct: 2
    },
    {
      id: 18,
      question: "Après une piqûre d'insecte, on surveille :",
      options: [
        "Les chaussures",
        "Une réaction allergique",
        "La couleur du t-shirt"
      ],
      correct: 1
    },
    {
      id: 19,
      question: "L'Activité 4 implique toujours :",
      options: [
        "Un protocole ou une collaboration médicale",
        "Un simple soin d'hygiène",
        "Un jeu éducatif"
      ],
      correct: 0
    },
    {
      id: 20,
      question: "La phrase correcte est :",
      options: [
        "« L'enfant n'était pas bien »",
        "« 15h20 – chute de 40 cm – pleurs immédiats – pas de perte de connaissance – parents informés »"
      ],
      correct: 1
    }
  ];

  const getSlideText = (slide) => {
    let text = '';
    if (slide.title) text += slide.title + '. ';
    if (slide.subtitle) text += slide.subtitle + '. ';
    if (slide.definition) text += slide.definition + ' ';
    if (slide.content) text += slide.content + ' ';
    if (slide.highlight) text += slide.highlight + ' ';
    if (slide.warning) text += 'Attention : ' + slide.warning + ' ';
    if (slide.note) text += slide.note + ' ';
    if (slide.message) text += slide.message + ' ';
    if (slide.nextStep) text += slide.nextStep + ' ';
    
    if (slide.items) {
      slide.items.forEach(item => {
        text += item.term + ': ' + item.desc + '. ';
      });
    }
    if (slide.extraInfo) {
      slide.extraInfo.forEach(info => {
        text += info.term + ': ' + info.desc + '. ';
      });
    }
    if (slide.tableData) {
      slide.tableData.forEach(row => {
        text += row.label + ': ' + row.value + '. ';
      });
    }
    if (slide.messages) {
      slide.messages.forEach(msg => {
        text += msg + '. ';
      });
    }
    if (slide.situations) {
      slide.situations.forEach(sit => {
        text += sit.title + ': ' + sit.desc + '. ';
      });
    }
    if (slide.steps) {
      slide.steps.forEach(step => {
        text += step.title + ': ' + step.items.join(', ') + '. ';
      });
    }
    if (slide.sections) {
      slide.sections.forEach(section => {
        text += section.title + ': ' + section.items.join(', ') + '. ';
      });
    }
    
    return text;
  };

  const handleListen = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      setIsLoading(false);
      return;
    }

    const slide = slides[currentSlide];
    let textToSpeak = getSlideText(slide);
    
    // Limit text length for faster TTS
    if (textToSpeak.length > 800) {
      textToSpeak = textToSpeak.substring(0, 800) + '...';
    }

    // Replace abbreviations
    textToSpeak = textToSpeak
      .replace(/DRP/g, 'désobstruction rhino-pharyngée')
      .replace(/PAI/g, 'projet d\'accueil individualisé')
      .replace(/IDE/g, 'infirmière diplômée d\'état')
      .replace(/RSAI/g, 'référent santé accueil inclusif')
      .replace(/SHA/g, 'solution hydroalcoolique')
      .replace(/DLC/g, 'date limite de consommation')
      .replace(/DLU/g, 'date limite d\'utilisation')
      .replace(/°C/g, ' degrés Celsius')
      .replace(/15/g, 'quinze')
      .replace(/SAMU/g, 'samu');

    if (!textToSpeak || textToSpeak.trim() === '') {
      toast.info("Pas de contenu audio pour cette slide");
      return;
    }

    try {
      setIsLoading(true);
      setIsPlaying(true);
      const API_URL = window.location.origin || '';
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToSpeak, speed: 1.1 })
      });

      if (!response.ok) throw new Error('TTS failed');

      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      
      setIsLoading(false);
      
      if (audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          toast.error("Erreur de lecture audio");
          setIsPlaying(false);
        };
        audioRef.current.play().catch(err => {
          console.error('Audio play error:', err);
          toast.error("Impossible de lire l'audio");
          setIsPlaying(false);
        });
      } else {
        toast.error("Pas de données audio reçues");
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error("Erreur lors de la génération audio");
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-title"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-center max-w-3xl opacity-90">{slide.subtitle}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white" data-testid="slide-sommaire">
            <h2 className="text-3xl font-bold mb-8 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {slide.sections.map((section, idx) => (
                <div 
                  key={idx}
                  onClick={() => section.slideTarget && goToSlide(section.slideTarget - 1)}
                  className="p-5 rounded-xl cursor-pointer transition-all hover:scale-105 hover:shadow-xl"
                  style={{ backgroundColor: section.color + '20', borderLeft: `4px solid ${section.color}` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: section.color }}
                    >
                      {section.num}
                    </span>
                    <h3 className="text-xl font-semibold">{section.title}</h3>
                  </div>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span style={{ color: section.color }}>•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-section"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-4 text-center">{slide.title}</h2>
            {slide.subtitle && <p className="text-xl md:text-2xl opacity-90">{slide.subtitle}</p>}
          </div>
        );

      case 'content':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-content">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-purple-400">{slide.title}</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 space-y-4">
                {slide.definition && (
                  <div className="p-4 bg-purple-500/20 rounded-xl border-l-4 border-purple-500">
                    <p className="text-lg font-medium">{slide.definition}</p>
                  </div>
                )}
                {slide.content && <p className="text-gray-300 text-lg leading-relaxed">{slide.content}</p>}
                {slide.highlight && (
                  <div className="p-4 bg-blue-500/20 rounded-xl border-l-4 border-blue-500">
                    <p className="text-blue-200">{slide.highlight}</p>
                  </div>
                )}
                {slide.warning && (
                  <div className="p-4 bg-red-500/20 rounded-xl border-l-4 border-red-500">
                    <p className="text-red-200 font-medium">{slide.warning}</p>
                  </div>
                )}
                {slide.extraInfo && (
                  <div className="space-y-3 mt-4">
                    {slide.extraInfo.map((info, idx) => (
                      <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                        <span className="font-semibold text-purple-300">{info.term} : </span>
                        <span className="text-gray-300">{info.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {slide.image && (
                <div className="lg:w-1/3">
                  <img src={slide.image} alt="" className="rounded-xl shadow-lg w-full h-48 lg:h-64 object-cover" />
                </div>
              )}
            </div>
          </div>
        );

      case 'list':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-list">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: slide.alertColor || '#A78BFA' }}>{slide.title}</h2>
            <div className="space-y-4">
              {slide.items.map((item, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: (slide.alertColor || '#8B5CF6') + '15', borderLeft: `4px solid ${slide.alertColor || '#8B5CF6'}` }}
                >
                  <span className="font-bold text-lg" style={{ color: slide.alertColor || '#A78BFA' }}>{item.term}</span>
                  <p className="text-gray-300 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
            {slide.warning && (
              <div className="mt-6 p-4 bg-red-500/20 rounded-xl border border-red-500/50 text-center">
                <p className="text-red-300 font-bold text-lg">{slide.warning}</p>
              </div>
            )}
          </div>
        );

      case 'listWithImage':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-list-image">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: slide.alertColor || '#A78BFA' }}>{slide.title}</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 space-y-3">
                {slide.items.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 rounded-xl transition-all hover:scale-[1.02]"
                    style={{ backgroundColor: (slide.alertColor || '#8B5CF6') + '15', borderLeft: `4px solid ${slide.alertColor || '#8B5CF6'}` }}
                  >
                    <span className="font-bold" style={{ color: slide.alertColor || '#A78BFA' }}>{item.term}</span>
                    <p className="text-gray-300 text-sm mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
              {slide.image && (
                <div className="lg:w-1/3 flex items-center justify-center">
                  <img src={slide.image} alt="" className="rounded-xl shadow-lg w-full h-auto max-h-80 object-cover" />
                </div>
              )}
            </div>
          </div>
        );

      case 'table':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-table">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-purple-400">{slide.title}</h2>
            {slide.subtitle && <p className="text-gray-400 mb-6">{slide.subtitle}</p>}
            <div className="space-y-3 mb-6">
              {slide.tableData.map((row, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-4 rounded-xl"
                  style={{ backgroundColor: row.color + '20', borderLeft: `4px solid ${row.color}` }}
                >
                  <span className="font-medium text-lg">{row.label}</span>
                  <span className="font-bold text-lg" style={{ color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
            {slide.alerts && (
              <div className="space-y-2">
                {slide.alerts.map((alert, idx) => (
                  <div key={idx} className="p-3 bg-amber-500/20 rounded-lg border-l-4 border-amber-500">
                    <p className="text-amber-200 text-sm">{alert}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'steps':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-steps">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-purple-400">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {slide.steps.map((step, idx) => (
                <div 
                  key={idx}
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: step.color + '20', borderTop: `4px solid ${step.color}` }}
                >
                  <div className="mb-4">
                    <span 
                      className="inline-block px-3 py-1 rounded-full text-white font-bold text-sm mb-2"
                      style={{ backgroundColor: step.color }}
                    >
                      {step.num}
                    </span>
                    <h3 className="font-bold text-lg">{step.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {step.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                        <span style={{ color: step.color }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'collaboration':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white" data-testid="slide-collaboration">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-purple-400">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {slide.situations.map((sit, idx) => (
                <div 
                  key={idx}
                  className="p-6 rounded-xl text-center"
                  style={{ backgroundColor: sit.color + '20', border: `2px solid ${sit.color}` }}
                >
                  <span className="text-4xl mb-4 block">{sit.icon}</span>
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-3"
                    style={{ backgroundColor: sit.color }}
                  >
                    {sit.num}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{sit.title}</h3>
                  <p className="text-gray-300 text-sm">{sit.desc}</p>
                </div>
              ))}
            </div>
            {slide.note && (
              <div className="p-4 bg-amber-500/20 rounded-xl border border-amber-500/50 text-center">
                <p className="text-amber-200 font-medium">{slide.note}</p>
              </div>
            )}
          </div>
        );

      case 'keyMessage':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-key-message"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8">{slide.title}</h2>
            <div className="space-y-4 max-w-2xl">
              {slide.messages.map((msg, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-lg">{msg}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'infographic':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-4 rounded-2xl text-white flex flex-col items-center justify-center" data-testid="slide-infographic">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-purple-400">{slide.title}</h2>
            <div className="flex-1 flex items-center justify-center w-full">
              <img 
                src={slide.image} 
                alt={slide.title} 
                className="max-h-[450px] w-auto rounded-xl shadow-2xl object-contain" 
              />
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-congratulations"
          >
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{slide.title}</h2>
            <p className="text-xl text-center max-w-2xl mb-8 opacity-90">{slide.message}</p>
            <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
              <p className="text-lg font-medium">{slide.nextStep}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (showQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8" data-testid="quiz-container">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              Quiz - Activité 4
            </h2>
            
            {quizSubmitted && (
              <div className={`mb-6 p-4 rounded-xl text-center ${quizScore >= 7 ? 'bg-green-500/20 border border-green-500' : 'bg-amber-500/20 border border-amber-500'}`}>
                <p className="text-2xl font-bold text-white">
                  Score : {quizScore} / {quizQuestions.length}
                </p>
                <p className={`${quizScore >= 7 ? 'text-green-300' : 'text-amber-300'}`}>
                  {quizScore >= 7 ? 'Excellent travail !' : 'Continuez à réviser !'}
                </p>
              </div>
            )}

            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {quizQuestions.map((q, qIdx) => (
                <div key={q.id} className="bg-slate-700/50 rounded-xl p-5">
                  <p className="text-white font-medium mb-4">
                    {qIdx + 1}. {q.question}
                  </p>
                  <div className="grid gap-2">
                    {q.options.map((option, oIdx) => {
                      let buttonClass = "w-full p-3 rounded-lg text-left transition-all ";
                      if (quizSubmitted) {
                        if (oIdx === q.correct) {
                          buttonClass += "bg-green-500/30 border-2 border-green-500 text-green-200";
                        } else if (quizAnswers[q.id] === oIdx && oIdx !== q.correct) {
                          buttonClass += "bg-red-500/30 border-2 border-red-500 text-red-200";
                        } else {
                          buttonClass += "bg-slate-600/50 text-gray-400";
                        }
                      } else {
                        buttonClass += quizAnswers[q.id] === oIdx 
                          ? "bg-purple-500/50 border-2 border-purple-500 text-white" 
                          : "bg-slate-600/50 hover:bg-slate-600 text-gray-300 border-2 border-transparent";
                      }
                      
                      return (
                        <button
                          key={oIdx}
                          onClick={() => handleQuizAnswer(q.id, oIdx)}
                          className={buttonClass}
                          disabled={quizSubmitted}
                          data-testid={`quiz-option-${q.id}-${oIdx}`}
                        >
                          <span className="flex items-center gap-3">
                            {quizSubmitted && oIdx === q.correct && <CheckCircle className="w-5 h-5 text-green-400" />}
                            {quizSubmitted && quizAnswers[q.id] === oIdx && oIdx !== q.correct && <XCircle className="w-5 h-5 text-red-400" />}
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowQuiz(false)}
                className="flex-1 py-3 px-6 bg-slate-600 hover:bg-slate-500 text-white rounded-xl font-medium transition-all"
                data-testid="back-to-course-btn"
              >
                Retour au cours
              </button>
              {!quizSubmitted ? (
                <button
                  onClick={submitQuiz}
                  disabled={Object.keys(quizAnswers).length < quizQuestions.length}
                  className="flex-1 py-3 px-6 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all"
                  data-testid="submit-quiz-btn"
                >
                  Valider ({Object.keys(quizAnswers).length}/{quizQuestions.length})
                </button>
              ) : (
                <button
                  onClick={resetQuiz}
                  className="flex-1 py-3 px-6 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-medium transition-all"
                  data-testid="retry-quiz-btn"
                >
                  Recommencer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8" data-testid="presentation-container">
      <div className="max-w-6xl mx-auto">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progression</span>
            <span>{currentSlide + 1} / {slides.length}</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Slide content */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden min-h-[500px]">
          {renderSlide(slides[currentSlide])}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all"
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Précédent
          </button>

          <button
            onClick={handleListen}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all ${
              isPlaying 
                ? 'bg-red-600 hover:bg-red-500' 
                : 'bg-purple-600 hover:bg-purple-500'
            } text-white`}
            data-testid="listen-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Chargement...
              </>
            ) : isPlaying ? (
              <>
                <VolumeX className="w-5 h-5" />
                Arrêter
              </>
            ) : (
              <>
                <Volume2 className="w-5 h-5" />
                Écouter
              </>
            )}
          </button>

          <button
            onClick={nextSlide}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition-all"
            data-testid="next-slide-btn"
          >
            {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DEAPActivite4Presentation;
